(() => {
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollToHash = (hash) => {
    if (!hash || hash === '#') return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!target) return;
    target.closest('[data-wnp-tabs]')?.dispatchEvent(new CustomEvent('wnp:activate-panel', {detail: {panel: target}}));
    target.scrollIntoView({behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start'});
    if (location.hash !== hash) history.pushState(null, '', hash);
  };

  function initialize(root) {
    if (root.dataset.enhanced) return;
    root.dataset.enhanced = 'true';
    root.querySelectorAll('[data-wnp-tabs]').forEach((tabsRoot) => {
      const tabs = [...tabsRoot.querySelectorAll('[data-wnp-tab]')];
      const panels = [...tabsRoot.querySelectorAll('[data-wnp-panel]')];
      const activate = (tab, focus = false) => {
        if (!tab) return;
        const panelId = tab.getAttribute('aria-controls');
        tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => { panel.hidden = panel.id !== panelId; });
        if (focus) tab.focus({preventScroll:true});
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = tabs.length - 1;
          activate(tabs[nextIndex], true);
          tabs[nextIndex].scrollIntoView({behavior: reduceMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest'});
        });
      });
      tabsRoot.addEventListener('wnp:activate-panel', (event) => {
        const tab = tabs.find((item) => item.getAttribute('aria-controls') === event.detail?.panel?.id);
        activate(tab);
      });
      const hashPanel = panels.find((panel) => `#${panel.id}` === location.hash);
      if (hashPanel) activate(tabs.find((tab) => tab.getAttribute('aria-controls') === hashPanel.id));
      else activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    });
    const gallery = root.querySelector('.wnp__evidence-gallery');
    const controls = root.querySelector('.wnp__gallery-controls');
    if (gallery && controls) {
      const slides = [...gallery.children];
      const previous = controls.querySelector('[data-gallery-prev]');
      const next = controls.querySelector('[data-gallery-next]');
      const count = controls.querySelector('[data-gallery-count]');
      let index = 0;
      let pending = false;
      const update = () => {
        index = Math.round(gallery.scrollLeft / (slides[0].getBoundingClientRect().width + 24));
        index = Math.max(0, Math.min(index, slides.length - 1));
        previous.disabled = index === 0;
        next.disabled = index === slides.length - 1;
        count.textContent = `${index + 1} / ${slides.length}`;
      };
      const move = (direction) => {
        const target = Math.max(0, Math.min(index + direction, slides.length - 1));
        gallery.scrollTo({left: target * (slides[0].getBoundingClientRect().width + 24), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      };
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      gallery.addEventListener('scroll', () => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(() => { update(); pending = false; });
      }, {passive:true});
      gallery.addEventListener('keydown', (event) => {
        if (event.target !== gallery || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      });
      controls.hidden = false;
      update();
    }
    const dialog = document.createElement('dialog');
    dialog.className = 'wnp__lightbox';
    const close = document.createElement('button');
    close.className = 'wnp__lightbox-close';
    close.type = 'button';
    close.textContent = 'Close ×';
    const image = document.createElement('img');
    const caption = document.createElement('p');
    const original = document.createElement('a');
    original.className = 'wnp__lightbox-link';
    original.textContent = 'Open full-size image (new tab)';
    original.target = '_blank';
    original.rel = 'noopener';
    caption.id = `${root.id}-image-caption`;
    dialog.setAttribute('aria-labelledby', caption.id);
    dialog.append(close, image, caption, original);
    root.append(dialog);
    let trigger = null;
    let previousOverflow = '';
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => {
      document.body.style.overflow = previousOverflow;
      trigger?.focus({preventScroll:true});
    });
    root.querySelectorAll('figure img').forEach((source) => {
      const button = document.createElement('button');
      button.className = 'wnp__zoom';
      button.type = 'button';
      button.setAttribute('aria-label', `Enlarge: ${source.alt}`);
      source.before(button);
      button.append(source);
      button.addEventListener('click', () => {
        trigger = button;
        image.src = source.currentSrc || source.src;
        original.href = image.src;
        image.alt = source.alt;
        caption.textContent = source.alt;
        previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialog.showModal();
      });
    });
    root.querySelectorAll('a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!document.getElementById(decodeURIComponent(link.hash.slice(1)))) return;
        event.preventDefault();
        scrollToHash(link.hash);
      });
    });
    const links = [...root.querySelectorAll('.wnp__jump a')];
    const jumpContainer = root.querySelector('.wnp__jump .wnp__container');
    let observer = null;
    if (jumpContainer && links.length) {
      const menu = document.createElement('div');
      menu.className = 'wnp__jump-menu';
      const select = document.createElement('select');
      select.setAttribute('aria-label','Explore this page');
      links.forEach((link) => {
        const option = document.createElement('option');
        option.value = link.hash;
        option.textContent = link.textContent.replace('↗','').trim();
        select.append(option);
      });
      select.addEventListener('change', () => scrollToHash(select.value));
      menu.append(select);
      jumpContainer.prepend(menu);
      const targets = links.map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1)))).filter(Boolean);
      observer = new IntersectionObserver((entries) => {
        const active = entries.filter((entry) => entry.isIntersecting).sort((a,b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (!active) return;
        select.value = `#${active.target.id}`;
        links.forEach((link) => {
          if (link.hash === `#${active.target.id}`) link.setAttribute('aria-current','location');
          else link.removeAttribute('aria-current');
        });
      }, {rootMargin:'-10% 0px -65% 0px'});
      targets.forEach((target) => observer.observe(target));
    }
    root.addEventListener('shopify:section:unload', () => {
      observer?.disconnect();
      if (dialog.open) dialog.close();
    }, {once:true});
  }
  document.querySelectorAll('.wnp').forEach(initialize);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('.wnp').forEach(initialize));
  window.addEventListener('hashchange', () => scrollToHash(location.hash));
})();
